import { createAction, props } from '@ngrx/store';

import {
  EntityCfg,
  EntityInterface
} from '@gfe/core/models/entities/entity-visualization-interface';

import {
  DataVisualizationState,
  FilterMethod,
  VisuComponentStates
} from '@core/models/data-visualization/data-visualization.state';
import { AuxiliaryDataContext } from '@core/services/auxiliary-data/auxiliary-data.service';

import { CalendarEvent } from '@features/components/visualization/calendar/models/calendar.models';
import { List } from '@features/components/visualization/list/models/list-visualization';
import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';
import { OccupationSeries } from '@features/components/visualization/occupation/models/occupation.model';
import { TimelineRow } from '@features/components/visualization/timeline/models/timeline';
import { TimelineModificationDescriptors } from '@features/components/visualization/timeline/models/timeline-modification-descriptor';

const ACTION_PREFIX = '[Data Visualization]';

export const setVisualizedEntities = createAction(
  `${ACTION_PREFIX} Set the entities to be visualized`,
  props<{
    entityCfg: EntityCfg<unknown>;
    entities: EntityInterface<unknown>[];
    auxContext?: AuxiliaryDataContext;
  }>()
);

/** Raised before a selection is resolved to its full payload; cleared when the state lands. */
export const setVisualizationHydrating = createAction(
  `${ACTION_PREFIX} Set the visualization hydrating flag`,
  props<{ hydrating: boolean }>()
);

export const setVisuComponentStates = createAction(
  `${ACTION_PREFIX} Update the visualization component states`,
  props<{ states: VisuComponentStates }>()
);

export const setGaliaRoutes = createAction(
  `${ACTION_PREFIX} Set Galia Routes`,
  props<{ routes: GaliaRoute[] }>()
);

export const setGaliaMarkers = createAction(
  `${ACTION_PREFIX} Set Galia Markers`,
  props<{ markers: GaliaMarker[] }>()
);

export const setTimelineRows = createAction(
  `${ACTION_PREFIX} Set Timeline Rows`,
  props<{ rows: TimelineRow[] }>()
);

export const setVisualizationLists = createAction(
  `${ACTION_PREFIX} Set Visualization Lists`,
  props<{ lists: List[] }>()
);

export const setCalendarEvents = createAction(
  `${ACTION_PREFIX} Set Calendar Events`,
  props<{ events: CalendarEvent[] }>()
);

export const setFilterOnAggregator = createAction(
  `${ACTION_PREFIX} Set Filter On Aggregator`,
  props<{
    filteredAggregatorIds: string[];
    filterMethod: FilterMethod;
    markerId?: string | undefined;
  }>()
);

/**
 * Pins or clears the focused action (the "job pin" a map or timeline click raises).
 *
 * Its own action rather than a field on `setFilterOnAggregator`: that action replaces the
 * whole aggregator filter, and a map click on a stop dispatches it for the same click that
 * raises a pin — so the pin needs a writer of its own.
 */
export const setActionFocus = createAction(
  `${ACTION_PREFIX} Set Action Focus`,
  props<{ actionUid: string | null }>()
);

export const setDataVisualizationState = createAction(
  `${ACTION_PREFIX} Set Data Visualization State`,
  props<{ newState: DataVisualizationState }>()
);

export const toggleCalendarVisualization = createAction(
  `${ACTION_PREFIX} Set Calendar Visualization State`,
  props<{ isOpen: boolean }>()
);

export const toggleTimelineVisualization = createAction(
  `${ACTION_PREFIX} Set Timeline Visualization State`,
  props<{ isOpen: boolean }>()
);

export const toggleListVisualization = createAction(
  `${ACTION_PREFIX} Set List Visualization State`,
  props<{ isOpen: boolean }>()
);

export const toggleMapVisualization = createAction(
  `${ACTION_PREFIX} Set Map Visualization State`,
  props<{ isOpen: boolean }>()
);

export const toggleOccupationVisualization = createAction(
  `${ACTION_PREFIX} Set Occupation Visualization State`,
  props<{ isOpen: boolean }>()
);

/**
 * Asks for the occupation at a set of sites. Raised by a surface becoming visible rather
 * than by a selection changing: the fetch is wide enough to be worth deferring until
 * something is actually going to draw it, and the popup needs one site where the module
 * needs every site in view.
 */
export const occupationRequested = createAction(
  `${ACTION_PREFIX} Occupation Requested`,
  props<{ siteUids: string[] }>()
);

/** The sites the hydrated selection puts on screen; what the module requests when opened. */
export const setOccupationSitesInView = createAction(
  `${ACTION_PREFIX} Set Occupation Sites In View`,
  props<{ siteUids: string[] }>()
);

export const setOccupationSeries = createAction(
  `${ACTION_PREFIX} Set Occupation Series`,
  props<{ series: OccupationSeries[] }>()
);

export const setOccupationLoading = createAction(
  `${ACTION_PREFIX} Set Occupation Loading`,
  props<{ loading: boolean }>()
);

export const entityModified = createAction(
  `${ACTION_PREFIX} Entity Modified`,
  props<{ entityType: string }>()
);

export const setTimelineTimeStep = createAction(
  `${ACTION_PREFIX} Set Timeline Time Step`,
  props<{ timeStep: number }>()
);

export const setTimelineModificationDescriptors = createAction(
  `${ACTION_PREFIX} Set Timeline Modification Descriptors`,
  props<{ descriptors: TimelineModificationDescriptors }>()
);

/**
 * A window without the live actions asking the window that has them to perform a write.
 * Carries `optionId` for a sub-options action and `selectedUid` for a multi-entity-select one.
 * `requesterId` names the window to report back to; every window sees the command, so without
 * it the outcome could not be addressed.
 */
export const applyTimelineModification = createAction(
  `${ACTION_PREFIX} Apply Timeline Modification`,
  props<{
    rowUid: string;
    actionId: string;
    requesterId: string;
    optionId?: string;
    selectedUid?: string;
  }>()
);

/**
 * Outcome of a write, reported back so the window the user is actually looking at can show it.
 * Title and message arrive already translated: composing them needs the cascade and
 * registration-repair results, which never leave the executing window. `requesterId` echoes the
 * command's, so a second popup does not report a write it never asked for.
 */
export const timelineModificationOutcome = createAction(
  `${ACTION_PREFIX} Timeline Modification Outcome`,
  props<{
    level: 'success' | 'warning' | 'error';
    title: string;
    message: string;
    requesterId: string;
  }>()
);
